/**
 * OSD Layout Component
 *
 * Wrapper component that:
 * 1. Provides OSD context to all nested routes
 * 2. Validates OSD from URL params
 * 3. Redirects to OSD selection page if OSD is invalid
 * 4. Renders nested routes with OSD context
 *
 * Route structure: /:osd/*
 * Example: /TAURON/kalkulatory/podstawowy
 */

import { useParams, Navigate, Outlet } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import { OSDProvider } from '../../contexts/OSDContext';
import { isValidOSDCode } from '../../utils/osdMetadata';

/**
 * OSD Layout Component
 *
 * Validates OSD from URL and wraps routes with OSDProvider
 */
export function OSDLayout() {
  const { osd, '*': restPath } = useParams<{ osd: string; '*': string }>();

  // If no OSD in URL, redirect to OSD selection
  if (!osd) {
    return <Navigate to="/" replace />;
  }

  // Normalize OSD to uppercase for case-insensitive URLs
  const normalizedOSD = osd.toUpperCase();

  // If OSD needs case normalization, redirect to uppercase URL
  if (osd !== normalizedOSD && isValidOSDCode(normalizedOSD)) {
    const newPath = restPath ? `/${normalizedOSD}/${restPath}` : `/${normalizedOSD}`;
    return <Navigate to={newPath} replace />;
  }

  // If invalid OSD code (even after normalization), redirect to OSD selection
  if (!isValidOSDCode(osd)) {
    return <Navigate to="/" replace />;
  }

  // Valid OSD - render with OSDProvider context
  return (
    <OSDProvider>
      <Outlet />
    </OSDProvider>
  );
}

/**
 * OSD Loading Component
 *
 * Shows loading spinner while OSD context is being initialized
 * Used internally by OSDLayout
 */
export function OSDLoading() {
  return (
    <Center style={{ height: '100vh' }}>
      <Loader size="lg" />
    </Center>
  );
}
